/**
 * /me/documents — read-only view of HR-uploaded employee documents.
 *
 * Mirrors the existing /me/payslips and /me/profile structure (child
 * of meLayoutRoute, inherits the employee sidebar). Read-only — the
 * employee can't upload or delete from this surface; both stay on
 * the admin side.
 *
 * Grouped by kind so the page reads cleanly even at ~20 documents.
 * Within each kind, newest-first.
 */
import { createRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  EmployeeDocumentKind,
  MyDocumentResponse,
} from '@kora/types';
import { meLayoutRoute } from './meLayout.js';
import { downloadMyDocument, listMyDocuments } from '../api/me.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';
import { formatBytes, formatDate } from '../lib/format.js';
import { DOCUMENT_KIND_PLURAL } from '../lib/documents.js';

export const myDocumentsRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: '/me/documents',
  component: MyDocumentsPage,
});

// Display order for the grouped sections. Mirrors the EmployeeDocumentKind
// enum's declaration order so the rendering is consistent with the admin
// upload form's kind dropdown.
const KIND_DISPLAY_ORDER: EmployeeDocumentKind[] = [
  'contract',
  'id_document',
  'certificate',
  'job_description',
  'performance_review',
  'other',
];

function MyDocumentsPage(): JSX.Element {
  const query = useQuery({
    queryKey: ['me', 'documents'],
    queryFn: listMyDocuments,
  });

  if (query.isLoading) {
    return (
      <section className="p-8 max-w-3xl">
        <div className="rounded border border-gray-900 p-8 text-center text-gray-500">
          Loading…
        </div>
      </section>
    );
  }
  if (query.isError || !query.data) {
    return (
      <section className="p-8 max-w-3xl">
        <Alert variant="error">
          Could not load your documents. Try refreshing — if the problem
          persists, contact HR.
        </Alert>
      </section>
    );
  }

  const documents = query.data.documents;
  const grouped = groupByKind(documents);

  return (
    <section className="p-8 space-y-6 max-w-3xl">
      <header className="space-y-2">
        <Link
          to="/me"
          className="text-xs text-gray-500 hover:text-white underline-offset-2 hover:underline"
        >
          ← Back to dashboard
        </Link>
        <h1 className="font-serif text-3xl">My documents</h1>
        <p className="text-sm text-gray-400">
          Contracts, certificates, IDs, and other documents HR has
          uploaded for you.
        </p>
      </header>

      {documents.length === 0 ? (
        <div className="rounded border border-gray-900 p-8 text-center text-gray-500 text-sm">
          No documents yet. HR will upload documents like your contract
          and certifications here as they&apos;re added to your file.
        </div>
      ) : (
        <div className="space-y-6">
          {KIND_DISPLAY_ORDER.map((kind) => {
            const group = grouped[kind] ?? [];
            if (group.length === 0) return null;
            return <KindSection key={kind} kind={kind} documents={group} />;
          })}
        </div>
      )}

      <p className="text-xs text-gray-500">
        Need a document corrected or removed? Contact HR.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Grouping + section
// ---------------------------------------------------------------------------

function groupByKind(
  documents: MyDocumentResponse[],
): Partial<Record<EmployeeDocumentKind, MyDocumentResponse[]>> {
  const out: Partial<Record<EmployeeDocumentKind, MyDocumentResponse[]>> = {};
  for (const d of documents) {
    const bucket = out[d.kind] ?? [];
    bucket.push(d);
    out[d.kind] = bucket;
  }
  return out;
}

function KindSection({
  kind,
  documents,
}: {
  kind: EmployeeDocumentKind;
  documents: MyDocumentResponse[];
}): JSX.Element {
  return (
    <div className="rounded border border-gray-900 divide-y divide-gray-900">
      <header className="px-4 py-3 text-xs uppercase tracking-wide text-gray-400">
        {DOCUMENT_KIND_PLURAL[kind]}
      </header>
      <ul>
        {documents.map((d) => (
          <DocumentRow key={d.id} document={d} />
        ))}
      </ul>
    </div>
  );
}

function DocumentRow({ document }: { document: MyDocumentResponse }): JSX.Element {
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (id: string) => downloadMyDocument(id),
    onSuccess: ({ blob, filename }) => {
      // Trigger a browser save via a hidden anchor. Filename comes from
      // the server's content-disposition so the operator's original
      // upload name is preserved.
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = filename || document.filename || 'download';
      window.document.body.appendChild(a);
      a.click();
      a.remove();
      // Free the object URL on the next tick — some browsers race the
      // download trigger with revoke if it's synchronous.
      setTimeout(() => URL.revokeObjectURL(url), 1_000);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 404) {
        setError('Document is no longer available. Refresh to update.');
      } else if (err instanceof ApiError && err.status === 502) {
        setError(
          'Document file is missing on our side. Contact HR.',
        );
      } else {
        setError('Could not download. Try again.');
      }
    },
  });

  return (
    <li className="px-4 py-3 flex items-center gap-4">
      <div className="flex-1 min-w-0 space-y-0.5">
        <p className="text-sm text-white truncate">{document.label}</p>
        <p className="text-xs text-gray-500 truncate">
          <span className="font-mono">{document.filename}</span>
          {' · '}
          {formatBytes(document.sizeBytes)}
          {' · uploaded '}
          {formatDate(document.uploadedAt)}
        </p>
        {error ? <Alert variant="error" compact className="mt-1">{error}</Alert> : null}
      </div>
      <button
        type="button"
        onClick={() => {
          setError(null);
          mutation.mutate(document.id);
        }}
        disabled={mutation.isPending}
        className="text-xs rounded border border-gray-700 px-3 py-1 hover:bg-gray-900 disabled:opacity-50 whitespace-nowrap"
      >
        {mutation.isPending ? 'Preparing…' : 'Download'}
      </button>
    </li>
  );
}
